import {
  Box3,
  type Group,
  InstancedBufferAttribute,
  InstancedInterleavedBuffer,
  InterleavedBufferAttribute,
  Sphere,
  Vector3,
} from 'three'
import type { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import type { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js'
import { EntityType } from '@/plugins/cad/module/CADModule/model/common/constant'
import type { IEntity } from '@/plugins/cad/module/CADModule/model/entity'
import { EntityObjectUtils } from '@/plugins/cad/module/CADModule/object/entityObject/entityObjectUtils'
import { createCadLineSegments2Object } from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import type { MaterialManager } from '@/plugins/cad/module/CADModule/object/material/MaterialManager'
import { installLineTypeDashScaleUpdater } from '@/plugins/cad/module/CADModule/object/objectManager/lineTypeDashScaleUpdater'
import {
  createStaticBatchDashMaterial,
  installStaticBatchDashScale,
} from '@/plugins/cad/module/CADModule/object/objectManager/staticBatchDashScale'
import { installStaticBatchRaycast } from '@/plugins/cad/module/CADModule/object/objectManager/staticBatchRaycast'

export interface StaticLineBatchRange {
  entityId: string
  entityType: EntityType
  batchId: string
  segmentStart: number
  segmentCount: number
  bounds: Box3
}

export interface StaticLineBatch {
  id: string
  object: LineSegments2
  ranges: readonly StaticLineBatchRange[]
}

export interface StaticLineBatchBuildResult {
  batches: readonly StaticLineBatch[]
  ranges: ReadonlyMap<string, StaticLineBatchRange>
  batchedEntityIds: ReadonlySet<string>
}

interface BatchState extends StaticLineBatch {
  signature: string
  capacity: number
  ownsMaterial: boolean
}

const BATCHABLE_TYPES = new Set<EntityType>([
  EntityType.Polyline,
  EntityType.Arc,
  EntityType.Circle,
  EntityType.Ellipse,
  EntityType.Spline,
])

function getEntityPoints(entity: IEntity) {
  const points = EntityObjectUtils.getEntitySamplePoints(entity) ?? []
  if (entity.type === EntityType.Ellipse) {
    const center = (entity as IEntity & { center: { x: number; y: number } }).center
    return points.map((point) => ({
      x: point.x + center.x,
      y: point.y + center.y,
      z: point.z ?? 0,
    }))
  }
  return points
}

/** 按兼容材质局部更新静态线，保留未变化批次及容量足够的 GPU buffer。 */
export class StaticLineBatchManager {
  private readonly _states = new Map<string, BatchState>()
  private _result: StaticLineBatchBuildResult = {
    batches: [],
    ranges: new Map(),
    batchedEntityIds: new Set(),
  }
  private _createdGeometries = 0
  private _disposedGeometries = 0
  private _bufferUpdates = 0

  constructor(
    private readonly _parent: Group,
    private readonly _materialManager: MaterialManager
  ) {}

  static isBatchable(entity: IEntity): boolean {
    return BATCHABLE_TYPES.has(entity.type)
  }

  rebuild(entities: readonly IEntity[]): StaticLineBatchBuildResult {
    const groups = new Map<
      string,
      { entities: IEntity[]; signatures: string[]; material: LineMaterial }
    >()
    for (const entity of entities) {
      if (!StaticLineBatchManager.isBatchable(entity)) continue
      const material = this._materialManager.getCachedMaterial(entity)
      // 材质实例身份保证所有 shader、透明度、深度及线端属性兼容。
      // 保留类型分组，便于诊断；动态 resolution/dashScale 不触发几何重建。
      const key = `${entity.type}|${material.uuid}`
      let group = groups.get(key)
      if (!group) {
        group = { entities: [], signatures: [], material }
        groups.set(key, group)
      }
      group.entities.push(entity)
      group.signatures.push(
        JSON.stringify([entity.id, EntityObjectUtils.getEntityGeometryKey(entity)])
      )
    }

    for (const [key, state] of this._states) {
      if (!groups.has(key)) {
        this.release(state)
        this._states.delete(key)
      }
    }

    for (const [key, group] of groups) {
      const signature = JSON.stringify(group.signatures)
      const previous = this._states.get(key)
      if (previous?.signature === signature) continue
      const positions: number[] = []
      const distances: number[] = []
      const references: number[] = []
      const ranges: StaticLineBatchRange[] = []
      const bounds = new Box3()
      const start = new Vector3()
      const end = new Vector3()
      let segmentCount = 0
      for (const entity of group.entities) {
        const points = getEntityPoints(entity)
        const entityBounds = new Box3()
        const segmentStart = segmentCount
        let distance = 0
        for (let index = 0; index + 1 < points.length; index += 1) {
          const a = points[index]!
          const b = points[index + 1]!
          start.set(a.x, a.y, a.z ?? 0)
          end.set(b.x, b.y, b.z ?? 0)
          if (start.equals(end)) continue
          positions.push(start.x, start.y, start.z, end.x, end.y, end.z)
          entityBounds.expandByPoint(start).expandByPoint(end)
          distances.push(distance)
          distance += start.distanceTo(end)
          distances.push(distance)
          segmentCount += 1
        }
        if (segmentStart === segmentCount) continue
        if (group.material.dashed) {
          const center = entityBounds.getCenter(new Vector3())
          for (let index = segmentStart; index < segmentCount; index += 1)
            references.push(center.x, center.y, center.z)
        }
        bounds.union(entityBounds)
        ranges.push({
          entityId: entity.id,
          entityType: entity.type,
          batchId: key,
          segmentStart,
          segmentCount: segmentCount - segmentStart,
          bounds: entityBounds,
        })
      }
      if (!segmentCount) {
        if (previous) this.release(previous)
        this._states.delete(key)
        continue
      }

      let state = previous
      if (!state || state.capacity < segmentCount) {
        if (state) this.release(state)
        const capacity = 2 ** Math.ceil(Math.log2(segmentCount))
        const geometry = new LineSegmentsGeometry().setPositions(new Float32Array(capacity * 6))
        const distanceBuffer = new InstancedInterleavedBuffer(new Float32Array(capacity * 2), 2, 1)
        geometry.setAttribute(
          'instanceDistanceStart',
          new InterleavedBufferAttribute(distanceBuffer, 1, 0)
        )
        geometry.setAttribute(
          'instanceDistanceEnd',
          new InterleavedBufferAttribute(distanceBuffer, 1, 1)
        )
        const ownsMaterial = group.material.dashed
        if (ownsMaterial)
          geometry.setAttribute(
            'instanceDashReference',
            new InstancedBufferAttribute(new Float32Array(capacity * 3), 3)
          )
        const object = createCadLineSegments2Object(
          geometry,
          ownsMaterial ? createStaticBatchDashMaterial(group.material) : group.material
        )
        installStaticBatchRaycast(object)
        installLineTypeDashScaleUpdater(object)
        installStaticBatchDashScale(object)
        object.layers.mask = this._parent.layers.mask
        object.userData.cadBatch = true
        object.userData.cadBatchStyleKey = key
        this._parent.add(object)
        state = { id: key, object, ranges: [], capacity, signature: '', ownsMaterial }
        this._states.set(key, state)
        this._createdGeometries += 1
      }
      const geometry = state.object.geometry
      const positionBuffer = (geometry.getAttribute('instanceStart') as InterleavedBufferAttribute)
        .data
      const distanceBuffer = (
        geometry.getAttribute('instanceDistanceStart') as InterleavedBufferAttribute
      ).data
      positionBuffer.array.set(positions)
      distanceBuffer.array.set(distances)
      if (state.ownsMaterial) {
        const referenceBuffer = geometry.getAttribute(
          'instanceDashReference'
        ) as InstancedBufferAttribute
        referenceBuffer.array.set(references)
        referenceBuffer.clearUpdateRanges()
        referenceBuffer.addUpdateRange(0, references.length)
        referenceBuffer.needsUpdate = true
      }
      for (const [buffer, count] of [
        [positionBuffer, positions.length],
        [distanceBuffer, distances.length],
      ] as const) {
        buffer.clearUpdateRanges()
        buffer.addUpdateRange(0, count)
        buffer.needsUpdate = true
      }
      geometry.instanceCount = segmentCount
      // 边界只覆盖有效段，容量尾部不参与裁剪或拾取。
      geometry.boundingBox = bounds
      geometry.boundingSphere = bounds.getBoundingSphere(new Sphere())
      state.object.userData.cadBatchEntityRanges = ranges
      state.ranges = ranges
      state.signature = signature
      this._bufferUpdates += 1
    }

    const ranges = new Map<string, StaticLineBatchRange>()
    const batches = [...this._states.values()]
    for (const batch of batches) {
      for (const range of batch.ranges) ranges.set(range.entityId, range)
    }
    this._result = { batches, ranges, batchedEntityIds: new Set(ranges.keys()) }
    return this._result
  }

  getResult(): StaticLineBatchBuildResult {
    return this._result
  }

  /** 按需读取资源计数，不在渲染热路径遍历实体。 */
  getResourceStats() {
    let capacityBytes = 0
    let activeSegments = 0
    for (const state of this._states.values()) {
      capacityBytes +=
        state.capacity * (state.ownsMaterial ? 11 : 8) * Float32Array.BYTES_PER_ELEMENT
      activeSegments += state.object.geometry.instanceCount
    }
    return {
      createdGeometries: this._createdGeometries,
      disposedGeometries: this._disposedGeometries,
      bufferUpdates: this._bufferUpdates,
      capacityBytes,
      activeSegments,
    }
  }

  private release(state: BatchState): void {
    state.object.removeFromParent()
    state.object.geometry.dispose()
    if (state.ownsMaterial) state.object.material.dispose()
    this._disposedGeometries += 1
  }

  dispose(): void {
    for (const state of this._states.values()) this.release(state)
    this._states.clear()
    this._result = { batches: [], ranges: new Map(), batchedEntityIds: new Set() }
  }
}
